import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Download, Filter, Search, Calendar,
  FileCheck, MoreVertical, Eye, FilePieChart
} from 'lucide-react';

const MOCK_REPORTS = [
  { id: 'REP-24-03-01', name: 'Báo cáo kiểm toán tổng thể Quý I/2026', type: 'PDF', org: 'Bộ TT&TT', size: '2.4 MB', date: '2026-03-20', author: 'Hệ thống (Tự động)' },
  { id: 'REP-24-03-02', name: 'Biên bản khắc phục vi phạm dữ liệu BYT', type: 'DOCX', org: 'Bộ Y tế', size: '156 KB', date: '2026-03-18', author: 'Lê Anh Vũ' },
  { id: 'REP-24-03-03', name: 'Thống kê chi tiết cảnh báo an ninh (Năm 2025)', type: 'XLSX', org: 'Bộ Công an', size: '14.2 MB', date: '2026-03-15', author: 'Hệ thống (Tự động)' },
  { id: 'REP-24-03-04', name: 'Kết quả đánh giá tuân thủ Nghị định 278', type: 'PDF', org: 'Toàn quốc', size: '3.1 MB', date: '2026-03-10', author: 'Phòng Pháp chế' },
  { id: 'REP-24-03-05', name: 'Tài liệu export raw data (Schema Diff)', type: 'JSON', org: 'Kho Bạc NN', size: '45.8 MB', date: '2026-03-05', author: 'Hệ thống (Tự động)' },
];

const EXT_COLORS: Record<string, { text: string; bg: string; border: string }> = {
  'PDF': { text: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
  'DOCX': { text: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  'XLSX': { text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  'JSON': { text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
};

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('all');

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative pb-10">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
         <div>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3 leading-tight font-heading">
              <FilePieChart className="text-indigo-600 dark:text-indigo-400" size={32} />
              Báo cáo & biên bản
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-2xl font-bold tracking-tight text-sm leading-relaxed">
              Kho lưu trữ số, xuất dữ liệu và quản lý các biên bản thẩm định có chữ ký số xác thực.
            </p>
         </div>
         <div className="flex gap-3">
            <button className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 text-[10px] font-black tracking-tight hover:bg-slate-50 dark:hover:bg-white/5 transition-all shadow-sm active:scale-95">
               <Filter size={16} /> Mẫu báo cáo
            </button>
            <button className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-[10px] font-black tracking-tight shadow-[0_10px_30px_rgba(99,102,241,0.3)] hover:shadow-[0_15px_40px_rgba(99,102,241,0.5)] transition-all transform hover:-translate-y-0.5 active:scale-95">
               <FileCheck size={18} /> Tạo báo cáo mới
            </button>
         </div>
      </div>

      {/* FILTER TABS & SEARCH */}
      <div className="glass-panel p-4 flex flex-col md:flex-row items-center justify-between gap-4 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm">
         <div className="flex gap-2 w-full md:w-auto overflow-x-auto pb-2 md:pb-0 scrollbar-none">
            {[
              { id: 'all', label: 'Kho báo cáo chung' },
              { id: 'pdf', label: 'PDF / Ký số' },
              { id: 'excel', label: 'Bảng tính' },
              { id: 'drafts', label: 'Bản nháp' },
            ].map(tab => (
              <button 
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-xl text-[10px] font-black tracking-tight transition-all border shadow-sm whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'bg-indigo-600 text-white border-indigo-400 shadow-indigo-500/20' 
                    : 'bg-slate-50 dark:bg-white/5 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-white/5 hover:bg-slate-100 dark:hover:bg-white/10'
                }`}
              >
                {tab.label}
              </button>
            ))}
         </div>
         <div className="flex items-center gap-2 bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/5 rounded-xl px-4 py-2.5 w-full md:w-72 focus-within:border-indigo-500/50 transition-all shadow-inner">
            <Search size={14} className="text-slate-400" />
            <input type="text" placeholder="Tìm kiếm tên báo cáo, ID..." className="bg-transparent text-xs outline-none w-full text-slate-900 dark:text-white font-bold" />
         </div>
      </div>

      {/* REPORTS GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
         <AnimatePresence mode="popLayout">
            {MOCK_REPORTS.map((report, idx) => (
              <motion.div 
                 layout
                 initial={{ opacity: 0, scale: 0.95, y: 20 }}
                 animate={{ opacity: 1, scale: 1, y: 0 }}
                 exit={{ opacity: 0, scale: 0.9 }}
                 transition={{ delay: idx * 0.1 }}
                 key={report.id}
                 className="glass-panel p-6 flex flex-col group cursor-pointer border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm hover:shadow-xl hover:border-indigo-500/30 dark:hover:border-indigo-500/30 transition-all relative overflow-hidden group rounded-[2rem]"
              >
                 <div className="absolute top-0 right-0 p-5 opacity-0 group-hover:opacity-100 transition-all z-10">
                    <button className="w-10 h-10 rounded-2xl bg-indigo-500 text-white flex items-center justify-center hover:bg-indigo-600 shadow-lg hover:shadow-indigo-500/50 transition-all transform hover:scale-110 active:scale-95">
                       <Download size={16} />
                    </button>
                 </div>

                 <div className="flex items-start gap-5 mb-6">
                    <div className={`w-16 h-16 rounded-[1.25rem] flex items-center justify-center font-black text-sm border shadow-inner ${EXT_COLORS[report.type].bg} ${EXT_COLORS[report.type].text} ${EXT_COLORS[report.type].border}`}>
                       {report.type}
                    </div>
                    <div className="flex-1">
                       <h3 className="text-slate-900 dark:text-white font-black leading-tight group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors line-clamp-2 tracking-tight text-sm font-heading">
                          {report.name}
                       </h3>
                       <p className="text-[10px] text-slate-400 dark:text-slate-500 font-mono mt-1 font-bold tracking-tight">ID: {report.id}</p>
                    </div>
                 </div>

                 <div className="mt-auto pt-5 border-t border-slate-100 dark:border-white/5 grid grid-cols-2 gap-y-4 gap-x-3">
                    <div>
                       <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-tight mb-1 opacity-60">Cơ quan thực hiện</p>
                       <p className="text-slate-700 dark:text-slate-300 font-black text-[11px] truncate tracking-tight">{report.org}</p>
                    </div>
                    <div>
                       <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-tight mb-1 opacity-60">Kích thước</p>
                       <p className="text-slate-700 dark:text-slate-300 font-black text-[11px] tracking-tight">{report.size}</p>
                    </div>
                    <div>
                       <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-tight mb-1 opacity-60">Người trích xuất</p>
                       <p className="text-indigo-600 dark:text-indigo-400 font-black text-[11px] truncate tracking-tight">{report.author}</p>
                    </div>
                    <div>
                       <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-tight mb-1 opacity-60 flex items-center gap-1.5">
                          <Calendar size={10} /> Ngày phát hành
                       </p>
                       <p className="text-slate-700 dark:text-slate-300 font-black text-[11px] tracking-tight">{report.date}</p>
                    </div>
                 </div>
                 
                 <div className="mt-6 flex gap-3">
                    <button className="flex-1 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-700 dark:text-white text-[10px] font-black py-3 rounded-2xl transition-all border border-slate-200 dark:border-white/5 flex items-center justify-center gap-2 tracking-tight shadow-sm transform active:scale-[0.98]">
                       <Eye size={14} /> Xem PDF
                    </button>
                    <button className="p-3 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-400 dark:text-slate-500 rounded-2xl transition-all border border-slate-200 dark:border-white/5 shadow-sm active:scale-95">
                       <MoreVertical size={16} />
                    </button>
                 </div>
              </motion.div>
            ))}
         </AnimatePresence>
      </div>
    </div>
  );
}
